import React, { useState, Component } from 'react';
import { Phone, MessageCircle, CheckCircle2, Clock, Shield, TrendingUp, Building, Home, Store, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { StatCard } from '../components/StatCard';
import { SEOSchema } from '../components/SEOSchema';
import { useLanguage } from '../contexts/LanguageContext';
export function ConsultationPage() {
  const {
    t,
    language,
    getSlug
  } = useLanguage();
  const getLanguageLink = (slug: string) => {
    const localizedSlug = getSlug(slug);
    return `/${language}/${localizedSlug}`;
  };
  const [formData, setFormData] = useState({
    name: '',
    whatsapp: '',
    location: '',
    propertyType: '',
    purchasePlan: '',
    timeline: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSuccess(false);
        setFormData({
          name: '',
          whatsapp: '',
          location: '',
          propertyType: '',
          purchasePlan: '',
          timeline: '',
          message: ''
        });
      }, 3000);
    }, 1000);
  };
  const handleWhatsAppClick = () => {
    const message = formData.name && formData.propertyType ? `Halo PT Graha Riau Gemilang, saya ${formData.name}. Saya ingin konsultasi ${formData.propertyType} di area ${formData.location || 'Riau'}. Rencana pembelian ${formData.purchasePlan || 'belum ditentukan'}. Perkiraan waktu ${formData.timeline || 'belum ditentukan'}. Mohon info unit yang tersedia.` : t('Halo PT Graha Riau Gemilang, saya ingin konsultasi properti. Mohon informasi lebih lanjut.', 'Hello PT Graha Riau Gemilang, I would like to consult about property. Please provide more information.');
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/62817203456?text=${encodedMessage}`, '_blank');
  };
  return <div className="pt-20 min-h-screen bg-gradient-to-b from-white to-gray-50" data-id="element-446">
      <SEOSchema type="contact" title={t('Konsultasi Properti | PT Graha Riau Gemilang', 'Property Consultation | PT Graha Riau Gemilang')} description={t('Dapatkan konsultasi gratis untuk hunian dan investasi properti di Riau. Developer terpercaya sejak 2005.', 'Get free consultation for housing and property investment in Riau. Trusted developer since 2005.')} breadcrumbs={[{
      name: t('Beranda', 'Home'),
      url: 'https://grahariaugemilang.com/'
    }, {
      name: t('Konsultasi', 'Consultation'),
      url: 'https://grahariaugemilang.com/konsultasi'
    }]} data-id="element-447" />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden" data-id="element-448">
        <div className="absolute inset-0 bg-gradient-to-br from-red-50 via-white to-gray-50" data-id="element-449"></div>
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-5" data-id="element-450">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-451"></div>
        </div>

        <div className="relative container-premium" data-id="element-452">
          <div className="max-w-4xl mx-auto text-center" data-id="element-453">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-white border border-red-100 shadow-sm mb-8 animate-fade-in" data-id="element-454">
              <Sparkles className="w-4 h-4 text-[#C4161C]" data-id="element-455" />
              <span className="text-sm font-semibold text-gray-700" data-id="element-456">
                {t('Konsultasi Gratis • Respon Cepat', 'Free Consultation • Fast Response')}
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 heading-premium animate-fade-in-up" data-id="element-457">
              {t('Konsultasi Hunian & Investasi Properti di Riau', 'Property Consultation in Riau—Housing & Investment')}
            </h1>

            <p className="text-xl text-gray-600 mb-6 leading-relaxed max-w-3xl mx-auto animate-fade-in-up" style={{
            animationDelay: '0.1s'
          }} data-id="element-458">
              {t('Dapatkan rekomendasi proyek yang sesuai kebutuhan Anda—rumah subsidi, hunian premium, hingga properti komersial—dengan proses yang jelas dan transparan.', 'Get tailored recommendations for subsidized housing, premium residences, or commercial properties—with a clear, transparent process.')}
            </p>

            <p className="text-sm text-gray-500 mb-8 animate-fade-in-up" style={{
            animationDelay: '0.2s'
          }} data-id="element-459">
              {t('Developer terpercaya sejak 2005 dengan puluhan proyek dan dukungan mitra perbankan.', 'A trusted developer since 2005 with proven projects and strong banking partners.')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-4 animate-fade-in-up" style={{
            animationDelay: '0.3s'
          }} data-id="element-460">
              <Button size="lg" onClick={handleWhatsAppClick} className="shadow-red hover:shadow-premium-lg group" data-id="element-461">
                <MessageCircle className="w-5 h-5 mr-2" data-id="element-462" />
                <span data-id="element-463">
                  {t('WhatsApp Konsultasi Cepat', 'WhatsApp Quick Consultation')}
                </span>
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-464" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => document.getElementById('consultation-form')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-465">
                {t('Isi Form Konsultasi', 'Submit Consultation Form')}
              </Button>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600" data-id="element-466">
              <div className="flex items-center" data-id="element-467">
                <CheckCircle2 className="w-4 h-4 text-green-600 mr-2" data-id="element-468" />
                <span data-id="element-469">{t('Respon cepat', 'Fast response')}</span>
              </div>
              <div className="flex items-center" data-id="element-470">
                <Clock className="w-4 h-4 text-green-600 mr-2" data-id="element-471" />
                <span data-id="element-472">{t('Jam kerja', 'Business hours')}</span>
              </div>
              <div className="flex items-center" data-id="element-473">
                <Shield className="w-4 h-4 text-green-600 mr-2" data-id="element-474" />
                <span data-id="element-475">
                  {t('Konsultasi tanpa biaya', 'No consultation fee')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section id="consultation-form" className="py-16 scroll-reveal" data-id="element-476">
        <div className="container-premium" data-id="element-477">
          <div className="max-w-4xl mx-auto" data-id="element-478">
            <div className="bg-white rounded-3xl shadow-premium-xl border border-gray-100 overflow-hidden" data-id="element-479">
              <div className="bg-gradient-to-r from-[#C4161C] to-[#8B0000] p-8 text-white" data-id="element-480">
                <h2 className="text-3xl font-bold mb-2 heading-premium" data-id="element-481">
                  {t('Ceritakan Kebutuhan Anda', 'Tell Us What You Need')}
                </h2>
                <p className="text-red-100" data-id="element-482">
                  {t('Tim kami akan membantu menemukan properti yang tepat untuk Anda', 'Our team will help you find the right property')}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="p-8 space-y-6" data-id="element-483">
                {isSuccess && <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-start animate-fade-in" data-id="element-484">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" data-id="element-485" />
                    <div data-id="element-486">
                      <p className="font-semibold text-green-900" data-id="element-487">
                        {t('Terima kasih!', 'Thank you!')}
                      </p>
                      <p className="text-sm text-green-700" data-id="element-488">
                        {t('Permintaan konsultasi Anda sudah kami terima. Tim kami akan menghubungi Anda segera.', 'Your consultation request has been received. Our team will contact you soon.')}
                      </p>
                    </div>
                  </div>}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-489">
                  <div data-id="element-490">
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-491">
                      {t('Nama Lengkap', 'Full Name')}{' '}
                      <span className="text-red-600" data-id="element-492">*</span>
                    </label>
                    <input type="text" id="name" required value={formData.name} onChange={e => setFormData({
                    ...formData,
                    name: e.target.value
                  })} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#C4161C] focus:border-transparent outline-none transition-all" placeholder={t('Masukkan nama lengkap', 'Enter your full name')} data-id="element-493" />
                  </div>

                  <div data-id="element-494">
                    <label htmlFor="whatsapp" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-495">
                      {t('Nomor WhatsApp', 'WhatsApp Number')}{' '}
                      <span className="text-red-600" data-id="element-496">*</span>
                    </label>
                    <input type="tel" id="whatsapp" required value={formData.whatsapp} onChange={e => setFormData({
                    ...formData,
                    whatsapp: e.target.value
                  })} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#C4161C] focus:border-transparent outline-none transition-all" placeholder="0812..." data-id="element-497" />
                  </div>
                </div>

                <div data-id="element-498">
                  <label htmlFor="location" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-499">
                    {t('Kota/Lokasi yang Diinginkan', 'Preferred Area')}{' '}
                    <span className="text-red-600" data-id="element-500">*</span>
                  </label>
                  <input type="text" id="location" required value={formData.location} onChange={e => setFormData({
                  ...formData,
                  location: e.target.value
                })} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#C4161C] focus:border-transparent outline-none transition-all" placeholder={t('Contoh: Pekanbaru, Kampar', 'Example: Pekanbaru, Kampar')} data-id="element-501" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-502">
                  <div data-id="element-503">
                    <label htmlFor="propertyType" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-504">
                      {t('Jenis Properti', 'Property Type')}{' '}
                      <span className="text-red-600" data-id="element-505">*</span>
                    </label>
                    <select id="propertyType" required value={formData.propertyType} onChange={e => setFormData({
                    ...formData,
                    propertyType: e.target.value
                  })} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#C4161C] focus:border-transparent outline-none transition-all" data-id="element-506">
                      <option value="" data-id="element-507">
                        {t('Pilih jenis properti', 'Select property type')}
                      </option>
                      <option value={t('Rumah Subsidi', 'Subsidized Housing')} data-id="element-508">
                        {t('Rumah Subsidi', 'Subsidized Housing')}
                      </option>
                      <option value={t('Rumah Premium', 'Premium Residence')} data-id="element-509">
                        {t('Rumah Premium', 'Premium Residence')}
                      </option>
                      <option value={t('Ruko/Kios', 'Shophouse/Kiosk')} data-id="element-510">
                        {t('Ruko/Kios', 'Shophouse/Kiosk')}
                      </option>
                      <option value={t('Komersial', 'Commercial')} data-id="element-511">
                        {t('Komersial', 'Commercial')}
                      </option>
                    </select>
                  </div>

                  <div data-id="element-512">
                    <label htmlFor="purchasePlan" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-513">
                      {t('Rencana Pembelian', 'Purchase Plan')}{' '}
                      <span className="text-red-600" data-id="element-514">*</span>
                    </label>
                    <select id="purchasePlan" required value={formData.purchasePlan} onChange={e => setFormData({
                    ...formData,
                    purchasePlan: e.target.value
                  })} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#C4161C] focus:border-transparent outline-none transition-all" data-id="element-515">
                      <option value="" data-id="element-516">
                        {t('Pilih rencana pembelian', 'Select purchase plan')}
                      </option>
                      <option value="KPR" data-id="element-517">{t('KPR', 'Mortgage')}</option>
                      <option value="Cash" data-id="element-518">Cash</option>
                      <option value={t('Investasi', 'Investment')} data-id="element-519">
                        {t('Investasi', 'Investment')}
                      </option>
                    </select>
                  </div>
                </div>

                <div data-id="element-520">
                  <label htmlFor="timeline" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-521">
                    {t('Perkiraan Waktu Beli', 'Purchase Timeline')}
                  </label>
                  <select id="timeline" value={formData.timeline} onChange={e => setFormData({
                  ...formData,
                  timeline: e.target.value
                })} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#C4161C] focus:border-transparent outline-none transition-all" data-id="element-522">
                    <option value="" data-id="element-523">
                      {t('Pilih waktu', 'Select timeline')}
                    </option>
                    <option value={t('0-1 bulan', '0-1 month')} data-id="element-524">
                      {t('0-1 bulan', '0-1 month')}
                    </option>
                    <option value={t('1-3 bulan', '1-3 months')} data-id="element-525">
                      {t('1-3 bulan', '1-3 months')}
                    </option>
                    <option value={t('3-6 bulan', '3-6 months')} data-id="element-526">
                      {t('3-6 bulan', '3-6 months')}
                    </option>
                    <option value={t('>6 bulan', '>6 months')} data-id="element-527">
                      {t('>6 bulan', '>6 months')}
                    </option>
                  </select>
                </div>

                <div data-id="element-528">
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-529">
                    {t('Pesan (Opsional)', 'Message (Optional)')}
                  </label>
                  <textarea id="message" rows={4} value={formData.message} onChange={e => setFormData({
                  ...formData,
                  message: e.target.value
                })} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#C4161C] focus:border-transparent outline-none transition-all resize-none" placeholder={t('Tipe rumah yang dicari, jumlah kamar, budget, dll.', 'Type of house, number of rooms, budget, etc.')} data-id="element-530" />
                </div>

                <div className="bg-gray-50 p-4 rounded-xl" data-id="element-531">
                  <p className="text-xs text-gray-600 leading-relaxed" data-id="element-532">
                    {t('Dengan mengirim, Anda setuju dihubungi oleh tim kami melalui WhatsApp/telepon untuk tindak lanjut konsultasi.', 'By submitting, you agree to be contacted via WhatsApp/phone for consultation follow-up.')}
                  </p>
                </div>

                <Button type="submit" size="lg" fullWidth disabled={isSubmitting} className="shadow-red hover:shadow-premium-lg" data-id="element-533">
                  {isSubmitting ? <span data-id="element-534">{t('Mengirim...', 'Sending...')}</span> : <span data-id="element-535">
                      {t('Kirim Permintaan Konsultasi', 'Send Consultation Request')}
                    </span>}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white scroll-reveal" data-id="element-536">
        <div className="container-premium" data-id="element-537">
          <div className="text-center mb-12" data-id="element-538">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 heading-premium" data-id="element-539">
              {t('Apa yang Anda Dapatkan dari Konsultasi', 'What You Will Get from Consultation')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto" data-id="element-540">
            {[{
            icon: Building,
            title: t('Rekomendasi proyek sesuai kebutuhan & lokasi', 'Project recommendations based on your needs')
          }, {
            icon: TrendingUp,
            title: t('Penjelasan skema pembelian (KPR/Cash)', 'Guidance on purchase options (Mortgage/Cash)')
          }, {
            icon: CheckCircle2,
            title: t('Panduan dokumen dan tahapan proses KPR', 'Document and mortgage process guidance')
          }, {
            icon: Home,
            title: t('Informasi ketersediaan unit & rencana serah terima', 'Unit availability and handover schedule')
          }, {
            icon: Shield,
            title: t('Konsultasi transparan, tanpa prosedur yang rumit', 'Transparent consultation without complicated procedures')
          }, {
            icon: Clock,
            title: t('Respon cepat dari tim profesional', 'Fast response from professional team')
          }].map((benefit, index) => <div key={index} className="flex items-start p-6 bg-gray-50 rounded-2xl border border-gray-100 hover:border-red-100 hover:bg-white transition-all duration-300 hover-lift" data-id="element-541">
                <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center text-[#C4161C] mr-4 flex-shrink-0" data-id="element-542">
                  <benefit.icon className="w-6 h-6" data-id="element-543" />
                </div>
                <p className="text-gray-700 font-medium leading-relaxed" data-id="element-544">
                  {benefit.title}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Property Types */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white scroll-reveal" data-id="element-545">
        <div className="container-premium" data-id="element-546">
          <div className="text-center mb-12" data-id="element-547">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 heading-premium" data-id="element-548">
              {t('Konsultasi Berdasarkan Kebutuhan Anda', 'Consult by Property Type')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-549">
            <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-premium hover:shadow-premium-lg transition-all duration-300 hover-lift text-center" data-id="element-550">
              <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 mx-auto mb-6" data-id="element-551">
                <Home className="w-8 h-8" data-id="element-552" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-553">
                {t('Rumah Subsidi', 'Subsidized Housing')}
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-554">
                {t('Hunian terjangkau dengan lokasi strategis dan fasilitas yang mendukung kebutuhan keluarga.', 'Affordable housing in strategic locations with complete facilities.')}
              </p>
              <Button variant="outline" onClick={handleWhatsAppClick} className="w-full" data-id="element-555">
                {t('Tanya Ketersediaan Unit', 'Ask Unit Availability')}
              </Button>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-premium hover:shadow-premium-lg transition-all duration-300 hover-lift text-center" data-id="element-556">
              <div className="w-16 h-16 rounded-2xl bg-purple-50 flex items-center justify-center text-purple-600 mx-auto mb-6" data-id="element-557">
                <Building className="w-8 h-8" data-id="element-558" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-559">
                {t('Rumah Premium', 'Premium Residences')}
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-560">
                {t('Hunian modern dengan kualitas bangunan terbaik dan desain fungsional.', 'Modern homes with superior quality and functional design.')}
              </p>
              <Button variant="outline" onClick={handleWhatsAppClick} className="w-full" data-id="element-561">
                {t('Tanya Ketersediaan Unit', 'Ask Unit Availability')}
              </Button>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-premium hover:shadow-premium-lg transition-all duration-300 hover-lift text-center" data-id="element-562">
              <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center text-amber-600 mx-auto mb-6" data-id="element-563">
                <Store className="w-8 h-8" data-id="element-564" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-565">
                {t('Komersial (Ruko/Kios)', 'Commercial Properties')}
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-566">
                {t('Ruang usaha di lokasi bernilai ekonomi untuk mendukung pertumbuhan bisnis.', 'Commercial spaces in prime locations for business growth.')}
              </p>
              <Button variant="outline" onClick={handleWhatsAppClick} className="w-full" data-id="element-567">
                {t('Tanya Ketersediaan Unit', 'Ask Unit Availability')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Stats */}
      <section className="py-16 bg-white scroll-reveal" data-id="element-568">
        <div className="container-premium" data-id="element-569">
          <div className="text-center mb-12" data-id="element-570">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 heading-premium" data-id="element-571">
              {t('Reputasi dan Pengalaman yang Teruji', 'Proven Track Record')}
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8" data-id="element-572">
            <div className="text-center p-6 bg-gray-50 rounded-2xl" data-id="element-573">
              <div className="text-4xl font-bold text-[#C4161C] mb-2" data-id="element-574">20+</div>
              <div className="text-sm text-gray-600" data-id="element-575">
                {t('Tahun Pengalaman', 'Years Experience')}
              </div>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-2xl" data-id="element-576">
              <div className="text-4xl font-bold text-[#C4161C] mb-2" data-id="element-577">26+</div>
              <div className="text-sm text-gray-600" data-id="element-578">
                {t('Proyek Selesai', 'Completed Projects')}
              </div>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-2xl" data-id="element-579">
              <div className="text-4xl font-bold text-[#C4161C] mb-2" data-id="element-580">
                1774+
              </div>
              <div className="text-sm text-gray-600" data-id="element-581">
                {t('Unit Properti', 'Property Units')}
              </div>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-2xl" data-id="element-582">
              <div className="text-4xl font-bold text-[#C4161C] mb-2" data-id="element-583">12+</div>
              <div className="text-sm text-gray-600" data-id="element-584">
                {t('Mitra Perbankan', 'Banking Partners')}
              </div>
            </div>
          </div>

          <p className="text-center text-gray-600 max-w-3xl mx-auto leading-relaxed" data-id="element-585">
            {t('Komitmen kami adalah kualitas bangunan, legalitas yang jelas, dan layanan purna jual yang responsif.', 'Our commitment is quality construction, clear legal compliance, and responsive after-sales service.')}
          </p>
        </div>
      </section>

      {/* Banking Partners */}
      <section className="py-16 bg-gray-50 scroll-reveal" data-id="element-586">
        <div className="container-premium" data-id="element-587">
          <div className="text-center mb-12" data-id="element-588">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 heading-premium" data-id="element-589">
              {t('Didukung Mitra Perbankan Terkemuka', 'Supported by Leading Banks')}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-590">
              {t('Kemudahan akses KPR/KPA dengan proses yang lebih cepat dan transparan.', 'Easy access to mortgages with faster and transparent processes.')}
            </p>
          </div>

          <div className="grid grid-cols-3 md:grid-cols-6 gap-6 max-w-4xl mx-auto" data-id="element-591">
            {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-16 bg-white rounded-xl flex items-center justify-center text-xs text-gray-400 font-bold border border-gray-100 hover:border-red-100 hover:bg-red-50/30 transition-all duration-300" data-id="element-592">
                BANK {i}
              </div>)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white scroll-reveal" data-id="element-593">
        <div className="container-premium" data-id="element-594">
          <div className="max-w-3xl mx-auto" data-id="element-595">
            <div className="text-center mb-12" data-id="element-596">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 heading-premium" data-id="element-597">
                {t('Pertanyaan yang Sering Diajukan', 'Frequently Asked Questions')}
              </h2>
            </div>

            <div className="space-y-4" data-id="element-598">
              {[{
              q: t('Apakah konsultasi dikenakan biaya?', 'Is there a consultation fee?'),
              a: t('Tidak. Konsultasi awal tidak dipungut biaya.', 'No. Initial consultation is free of charge.')
            }, {
              q: t('Bisa dibantu proses KPR?', 'Can you help with the mortgage process?'),
              a: t('Ya. Tim kami membantu arahan tahapan, dokumen, hingga proses pengajuan sesuai kebijakan bank.', 'Yes. Our team assists with guidance, documents, and submission process according to bank policies.')
            }, {
              q: t('Berapa lama respon setelah isi form?', 'How long is the response time?'),
              a: t('Umumnya dalam 24 jam pada jam kerja.', 'Usually within 24 hours during business hours.')
            }, {
              q: t('Apakah legalitas aman?', 'Is the legal documentation secure?'),
              a: t('Kami mengutamakan legalitas dan proses yang transparan sesuai ketentuan yang berlaku.', 'We prioritize legal compliance and transparent processes according to regulations.')
            }].map((faq, index) => <div key={index} className="bg-gray-50 p-6 rounded-2xl border border-gray-100 hover:border-red-100 transition-all duration-300" data-id="element-599">
                  <h3 className="font-bold text-gray-900 mb-2" data-id="element-600">{faq.q}</h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-601">{faq.a}</p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-[#C4161C] to-[#8B0000] scroll-reveal" data-id="element-602">
        <div className="container-premium text-center text-white" data-id="element-603">
          <h2 className="text-4xl font-bold mb-6 heading-premium" data-id="element-604">
            {t('Siap Mulai Konsultasi?', 'Ready to Start Your Consultation?')}
          </h2>
          <p className="text-xl text-red-100 mb-10 max-w-2xl mx-auto" data-id="element-605">
            {t('Klik WhatsApp untuk respon cepat, atau isi form agar kami menyiapkan rekomendasi yang paling sesuai.', 'Click WhatsApp for quick response, or fill the form for tailored recommendations.')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-606">
            <Button size="lg" onClick={handleWhatsAppClick} className="bg-white text-[#C4161C] hover:bg-gray-100 border-none shadow-premium-lg group" data-id="element-607">
              <MessageCircle className="w-5 h-5 mr-2" data-id="element-608" />
              <span data-id="element-609">{t('WhatsApp Sekarang', 'WhatsApp Now')}</span>
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-610" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => document.getElementById('consultation-form')?.scrollIntoView({
            behavior: 'smooth'
          })} className="border-2 border-white text-white hover:bg-white hover:text-[#C4161C]" data-id="element-611">
              {t('Isi Form Konsultasi', 'Fill Consultation Form')}
            </Button>
          </div>

          {/* Contact Info */}
          <div className="mt-12 pt-8 border-t border-white/20" data-id="element-612">
            <p className="text-sm text-red-100 mb-4" data-id="element-613">
              {t('Atau hubungi kami langsung:', 'Or contact us directly:')}
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm" data-id="element-614">
              <a href="tel:+6276121493" className="flex items-center hover:text-red-100 transition-colors" data-id="element-615">
                <Phone className="w-4 h-4 mr-2" data-id="element-616" />
                0761-21493
              </a>
              <a href="tel:+6276147961" className="flex items-center hover:text-red-100 transition-colors" data-id="element-617">
                <Phone className="w-4 h-4 mr-2" data-id="element-618" />
                0761-47961
              </a>
              <a href="https://wa.me/6281365631901" target="_blank" rel="noopener noreferrer" className="flex items-center hover:text-red-100 transition-colors" data-id="element-619">
                <MessageCircle className="w-4 h-4 mr-2" data-id="element-620" />
                +62 813-6563-1901
              </a>
              <a href="https://wa.me/62817203456" target="_blank" rel="noopener noreferrer" className="flex items-center hover:text-red-100 transition-colors" data-id="element-621">
                <MessageCircle className="w-4 h-4 mr-2" data-id="element-622" />
                +62 817-203-456
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>;
}